import { ChainURL, EnrichedChainURL } from '@ankr.com/chains-list';

import { ChainGroupID, EndpointGroup } from '../types';

// group exceptions list includes extension only chains that should be grouped
const GROUP_EXCEPTIONS = [
  ChainGroupID.FLARE_COSTON,
  ChainGroupID.FLARE_COSTON2,
  ChainGroupID.FLARE_SONGBIRD,
];

// always merge even if !isValidExtensionGroup
const ALWAYS_MERGE_EXCEPTIONS = [ChainGroupID.BTC, ChainGroupID.BTC_BLOCKBOOK];
/**
 * Validates if a group consists of valid extension chains.
 * Logic: Checks chains that have paths, ensuring they are of type 'extension'.
 */
const isValidExtensionGroup = (group: EndpointGroup): boolean => {
  if (GROUP_EXCEPTIONS.includes(group.id)) {
    return false;
  }

  if (ALWAYS_MERGE_EXCEPTIONS.includes(group.id)) {
    return true;
  }

  const chainsWithPaths = group.chains.filter(
    chain => chain.paths && chain.paths.length > 0,
  );

  return chainsWithPaths.every(chain => chain.type === 'extension');
};

/**
 * Finds the chain within a group that owns the specific URL.
 * matches by RPC URL which is required unique identifier.
 */
const findChainByUrl = (
  group: EndpointGroup,
  url: ChainURL | EnrichedChainURL,
) => {
  return group.chains.find(chain =>
    chain.urls.some(
      chainUrl => chainUrl.rpc === url.rpc || chainUrl.rest === url.rest,
    ),
  );
};

/**
 * Enriches and deduplicates URLs from multiple groups.
 * Attaches source metadata (id, chainName) from the first occurrence.
 */
const enrichUrls = (groups: EndpointGroup[]): EnrichedChainURL[] => {
  const seenUrls = new Map<string, EnrichedChainURL>();

  groups.forEach(group => {
    group.urls.forEach(url => {
      const key = JSON.stringify(url);

      if (!seenUrls.has(key)) {
        const foundChain = findChainByUrl(group, url);

        seenUrls.set(key, {
          ...url,
          id: group.id,
          name: foundChain?.name || group.name,
        });
      }
    });
  });

  return Array.from(seenUrls.values());
};

/**
 * Aggregates all unique chains from the provided groups.
 */
const gatherChains = (groups: EndpointGroup[]) => {
  const chainMap = new Map();

  groups.forEach(group => {
    group.chains.forEach(chain => chainMap.set(chain.id, chain));
  });

  return Array.from(chainMap.values());
};

export const mergeExtensions = (groups: EndpointGroup[]): EndpointGroup[] => {
  // Only merge if ALL groups satisfy the extension criteria
  if (!groups.every(isValidExtensionGroup)) {
    return groups;
  }

  const mergedUrls = enrichUrls(groups);

  if (mergedUrls.length === 0) {
    return groups;
  }

  const mergedGroup: EndpointGroup = {
    id: ChainGroupID.MERGED,
    name: 'All Networks',
    pluralName: 'All Networks',
    chainName: 'All Networks',
    chains: gatherChains(groups),
    urls: mergedUrls,
    urlsCount: mergedUrls.length,
  };

  return [mergedGroup];
};
